// WebSocket + polling hook for real-time notifications and emergency alerts.
// Extracted from AuthProvider to keep it lean. Behaviour is identical:
//   • Fetches an initial snapshot on connect (doPoll)
//   • Listens for {notification|emergency|emergency_dismiss|task_update} frames
//   • Exponential-backoff reconnect
//   • 30s REST poll as a safety net if a WS frame is lost
//   • Fires an OS-level Notification if the user has opted-in and the tab is hidden

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { playBeep, playEmergencyBeep, API } from "./api";
import { getToken } from "./tokenStorage";

const MAX_NOTES = 100;
const POLL_INTERVAL_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;

function fireBrowserNotification(n) {
  try {
    if (typeof window !== "undefined"
      && "Notification" in window
      && Notification.permission === "granted"
      && document.visibilityState !== "visible") {
      const note = new Notification("Aii Group", {
        body: n.message,
        icon: "/aii-logo.png",
        tag: n.id || n.kind,
      });
      note.onclick = () => { window.focus(); note.close(); };
    }
  } catch (err) {
    console.debug("Notification API failed:", err?.message);
  }
}

function buildWsUrl() {
  const token = getToken();
  if (!token) return null;
  const wsProto = API.startsWith("https") ? "wss" : "ws";
  const host = API.replace(/^https?:\/\//, "").replace(/\/api$/, "");
  return `${wsProto}://${host}/api/ws?token=${encodeURIComponent(token)}`;
}

export function useNotificationSocket(user) {
  const [notifications, setNotifications] = useState([]);
  const [emergency, setEmergency] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  const lastNoteIdRef = useRef(null);
  const lastEmergencyIdRef = useRef(null);
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);

  const doPoll = useCallback(async () => {
    try {
      const [nRes, eRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/emergency/active"),
      ]);
      const notes = nRes.data || [];
      setNotifications(notes);
      const newest = notes.find((n) => !n.read);
      if (newest && newest.id !== lastNoteIdRef.current) {
        if (lastNoteIdRef.current !== null) {
          playBeep(180, 720);
          if (newest.kind !== "emergency") toast.success(newest.message);
        }
        lastNoteIdRef.current = newest.id;
      }
      const ev = eRes.data;
      if (ev && ev.id !== lastEmergencyIdRef.current) {
        setEmergency(ev);
        if (lastEmergencyIdRef.current !== null) playEmergencyBeep();
        lastEmergencyIdRef.current = ev.id;
      } else if (!ev) {
        setEmergency(null);
      }
    } catch (e) {
      console.debug("Poll failed:", e?.message);
    }
  }, []);

  const handleFrame = useCallback((msg) => {
    if (msg.type === "notification") {
      const n = msg.data;
      setNotifications((prev) => [n, ...prev].slice(0, MAX_NOTES));
      playBeep(180, 720);
      if (n.kind !== "emergency") toast.success(n.message);
      fireBrowserNotification(n);
    } else if (msg.type === "emergency") {
      setEmergency(msg.data);
      playEmergencyBeep();
      lastEmergencyIdRef.current = msg.data.id;
    } else if (msg.type === "emergency_dismiss") {
      setEmergency(null);
    } else if (msg.type === "task_update") {
      window.dispatchEvent(new CustomEvent("aii:task_update", { detail: msg.data }));
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let mounted = true;

    const connectWs = () => {
      const url = buildWsUrl();
      if (!url) return;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          if (!mounted) { ws.close(); return; }
          setWsConnected(true);
          wsRetryRef.current = 0;
          doPoll();
        };
        ws.onmessage = (e) => {
          if (!mounted) return;
          try { handleFrame(JSON.parse(e.data)); }
          catch (err) { console.debug("WS message parse failed:", err?.message); }
        };
        ws.onclose = () => {
          setWsConnected(false);
          if (!mounted) return;
          const delay = Math.min(MAX_BACKOFF_MS, 1500 * Math.pow(2, wsRetryRef.current++));
          setTimeout(connectWs, delay);
        };
        ws.onerror = () => {
          try { ws.close(); }
          catch (closeErr) { console.debug("WS close on error failed:", closeErr?.message); }
        };
      } catch (e) {
        console.debug("WS connect failed:", e?.message);
        setTimeout(connectWs, 5000);
      }
    };

    connectWs();
    doPoll();
    const timer = setInterval(doPoll, POLL_INTERVAL_MS);
    return () => {
      mounted = false;
      clearInterval(timer);
      if (wsRef.current) {
        try { wsRef.current.close(); }
        catch (closeErr) { console.debug("WS cleanup close failed:", closeErr?.message); }
      }
    };
  }, [user, doPoll, handleFrame]);

  const refreshNotifications = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setNotifications(data);
  }, []);

  return {
    notifications, setNotifications,
    emergency, setEmergency,
    wsConnected,
    refreshNotifications,
  };
}
